import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CloseButton,
  Group,
  Paper,
  Portal,
  Stack,
  Text,
  ThemeIcon,
  rem,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  selectEyeTrackingFailure,
  selectShouldShowSystemOverlayWarning,
} from "@store/connection/connectionSelectors";
import { clearEyeTrackingFailure } from "@store/connection/connectionSlice";
import { clearRunStateForNewSession, requestHeadsetReboot } from "@utils/headsetRestart";
import { persistor } from "@store/index";

export const NEW_SESSION_URL = "/new-test";

const STACKED_BOTTOM = 132;
const BOTTOM = 16;

const EyeTrackingRestartBanner: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const failure = useSelector(selectEyeTrackingFailure);
  const isOverlayWarningShown = useSelector(selectShouldShowSystemOverlayWarning);
  const [isRestarting, setIsRestarting] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const { pathname } = useLocation();
  const shownOnPathname = useRef(pathname);

  useEffect(() => {
    if (shownOnPathname.current === pathname) return;
    shownOnPathname.current = pathname;
    dispatch(clearEyeTrackingFailure());
  }, [dispatch, pathname]);

  if (!failure) return null;

  const isWarning = failure.severity === "warning";
  const tint = isWarning ? "yellow" : "red";
  const accent = isWarning ? "orange" : "red";
  const textColor = `${accent}.9`;

  const handleRestart = async () => {
    setIsRestarting(true);
    setHasFailed(false);
    try {
      await requestHeadsetReboot();
    } catch (e) {
      console.error("Eye tracking restart banner: reboot request failed:", e);
      setHasFailed(true);
      setIsRestarting(false);
      return;
    }
    clearRunStateForNewSession(dispatch);
    await persistor.flush().catch(() => undefined);
    window.location.href = NEW_SESSION_URL;
  };

  return (
    <Portal>
      <Paper
        p="md"
        radius="md"
        withBorder
        shadow="md"
        style={{
          position: "fixed",
          bottom: isOverlayWarningShown ? STACKED_BOTTOM : BOTTOM,
          left: BOTTOM,
          zIndex: 10001,
          width: `min(${rem(420)}, calc(100vw - ${rem(32)}))`,
          backgroundColor: `var(--oculera-tint-${tint})`,
          borderColor: `var(--oculera-tint-${tint}-border)`,
        }}
      >
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <ThemeIcon size={28} radius="xl" variant="light" color={accent}>
            <IconAlertTriangle size={18} />
          </ThemeIcon>
          <Stack gap="xs" style={{ flex: 1 }}>
            <div>
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Text size="sm" fw={600} c={textColor}>
                  {isWarning ? t("EyeTrackingFailure.warningTitle") : t("EyeTrackingFailure.title")}
                </Text>
                <CloseButton
                  size="sm"
                  c={textColor}
                  aria-label={t("close")}
                  onClick={() => dispatch(clearEyeTrackingFailure())}
                />
              </Group>
              <Text size="sm" c={textColor}>
                {hasFailed
                  ? t("EyeTrackingFailure.restartFailed")
                  : isWarning
                    ? t("EyeTrackingFailure.warningMessage")
                    : t("EyeTrackingFailure.message")}
              </Text>
            </div>
            {!isWarning && (
              <Group justify="flex-end">
                <Button size="xs" color="red" loading={isRestarting} onClick={handleRestart}>
                  {t("EyeTrackingFailure.restartDevice")}
                </Button>
              </Group>
            )}
          </Stack>
        </Group>
      </Paper>
    </Portal>
  );
};

export default EyeTrackingRestartBanner;
